import type {
  ActivityFilters,
  ActivityItem,
  ActivityPage,
  ActivityPerson,
  ActivityStatus,
  ActivitySummary,
  ActivityType,
  AgentsPageAccess,
} from './types';
import { str } from './wireValues';

// Wire readers for the Agents page's activity endpoints (OMT-1813). Wire data
// is untrusted: malformed entries are dropped, never thrown on.

type Obj = Record<string, unknown>;

const obj = (v: unknown): Obj | null =>
  typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Obj) : null;
const list = (v: unknown): unknown[] => (Array.isArray(v) ? v : []);
const present = <T>(v: T | null): v is T => v !== null;

const TYPES: readonly string[] = [
  'chat',
  'crew',
  'pricing_import',
  'skill_publish',
  'skill_rollback',
  'triage',
  'mimir_send',
  'mimir_ask',
];
const STATUSES: readonly string[] = [
  'running',
  'completed',
  'failed',
  'interrupted',
];

export function toAgentsPageAccess(body: unknown): AgentsPageAccess | null {
  const o = obj(body);
  if (!o || typeof o.allowed !== 'boolean') return null;
  return { allowed: o.allowed, history: o.history === 'on' ? 'on' : 'off' };
}

export function toActivitySummary(v: unknown): ActivitySummary | null {
  const o = obj(v);
  const id = o && str(o.id);
  const type = o && str(o.type);
  const status = o && str(o.status);
  const startedAt = o && str(o.startedAt);
  if (!o || !id || !type || !status || !startedAt) return null;
  if (!TYPES.includes(type) || !STATUSES.includes(status)) return null;
  return {
    id,
    type: type as ActivityType,
    status: status as ActivityStatus,
    outcome: str(o.outcome) ?? null,
    errorCode: str(o.errorCode) ?? null,
    startedAt,
    endedAt: str(o.endedAt) ?? null,
    userId: str(o.userId) ?? null,
    userEmail: str(o.userEmail) ?? null,
    userName: str(o.userName) ?? null,
    assessmentId: str(o.assessmentId) ?? null,
    agent: str(o.agent) ?? null,
    tokens: typeof o.tokens === 'number' ? o.tokens : 0,
  };
}

const record = <T>(
  v: unknown,
  read: (entry: Obj) => T | null,
): Record<string, T> => {
  const out: Record<string, T> = {};
  const o = obj(v) ?? {};
  for (const [key, entry] of Object.entries(o)) {
    const e = obj(entry);
    const read1 = e && read(e);
    if (read1) out[key] = read1;
  }
  return out;
};

export function toActivityItem(v: unknown): ActivityItem | null {
  const summary = toActivitySummary(v);
  const o = obj(v);
  if (!summary || !o) return null;
  return {
    ...summary,
    source: str(o.source) ?? '',
    gitSha: str(o.gitSha) ?? '',
    skillVersions: record(o.skillVersions, (e) => {
      const version = str(e.version);
      return version ? { version, draft: e.draft === true } : null;
    }),
    models: record(o.models, (e) => {
      const provider = str(e.provider);
      const model = str(e.model);
      return provider && model ? { provider, model } : null;
    }),
    detail: obj(o.detail) ?? {},
  };
}

const toPerson = (v: unknown): ActivityPerson | null => {
  const o = obj(v);
  const email = o && str(o.email);
  if (!o || !email) return null;
  return { email, name: str(o.name) ?? null };
};

export function toActivityPage(body: unknown): ActivityPage | null {
  const o = obj(body);
  if (!o) return null;
  return {
    history: o.history === 'on' ? 'on' : 'off',
    items: list(o.items).map(toActivitySummary).filter(present),
    nextCursor: str(o.nextCursor) ?? null,
    people: list(o.people).map(toPerson).filter(present),
  };
}

/** The GET /activity query string: fixed order, nulls left out. */
export function activityQuery(
  filters: ActivityFilters,
  cursor: string | null,
): string {
  const params = new URLSearchParams();
  const add = (key: string, value: string | null) => {
    if (value) params.set(key, value);
  };
  add('type', filters.type);
  add('outcome', filters.outcome);
  add('started_by', filters.startedBy);
  add('since', filters.since);
  add('until', filters.until);
  add('cursor', cursor);
  return params.toString();
}
